import { type PaletteKey, type PixelCanvas, sprite } from '../../../lib';
import { box, byRow, geometry, outlined, taper } from '../effect-guns.sprite';

/**
 * Arbalest (the Knight set's gun, ADR-0160 amended): a steel crossbow held level. A walnut stock at its back with a
 * steel butt plate, a long tiller to the bow with a gold-capped nut where the string catches, the steel bow standing
 * across it near the muzzle (its limbs swept back to their tips, gold caps on them), the string drawn back from the
 * tips to the nut, and a bolt laid on the tiller, its gold head at the muzzle. On a shot (`fire`) the string snaps
 * straight across the bow and the bolt is gone, then it is drawn and loaded again.
 */

const WALNUT = ['N', 'N', 'n', 'm'] as const;

/** The bow's limb at row y (its middle at 20): x of its front edge, swept back toward its tips by `draw` px. */
const limb = (y: number, drawn: boolean) => 47 - ((y - 20) / 15) ** 2 * (drawn ? 6 : 2.5);

const arbalest = (glint: number, state: 'drawn' | 'loose' | 'cocking') =>
  outlined((c: PixelCanvas) => {
    const drawn = state !== 'loose';
    // The stock: walnut, lit on top, its butt plate of steel.
    box(c, 2, 16, 24, 25, [2, 1], byRow(16, 25, WALNUT, 1, 2), 'l');
    box(c, 2, 17, 3, 24, [1], (_x, y): PaletteKey => (y < 19 ? '5' : y > 22 ? '3' : '4'), 'l');
    // The tiller: a long walnut beam to the bow, a groove along its top where the bolt lies.
    taper(c, 25, 47, 21, 2, 2, (top, bottom): PaletteKey => (top === 0 ? 'm' : bottom === 0 ? 'N' : 'n'));
    // The trigger lever under the stock, long, steel.
    box(c, 14, 26, 22, 27, [0], (x): PaletteKey => (x < 17 ? '4' : '3'));
    // The nut where the string catches: a gold cap.
    box(c, 29, 17, 31, 19, [0], (x, y): PaletteKey => (y === 17 ? 'y' : x === 31 ? 'U' : 'Y'));
    // The string from the limbs' tips back to the nut (drawn), or straight across the bow (loosed).
    for (let y = 6; y <= 34; y++) {
      const tipX = limb(y, drawn) - 1;
      if (y === 20 || y === 21) continue;
      const x = drawn ? Math.round(30 + ((tipX - 30) * Math.abs(y - 20)) / 14) : Math.round(tipX);
      c.set(x, y, '5');
    }
    // The bow: steel limbs swept back to their tips, lit on their left, gold-capped tips.
    for (let y = 5; y <= 35; y++) {
      const x = Math.round(limb(y, drawn));
      const cap = y < 7 || y > 33;
      c.set(x, y, cap ? 'y' : '5');
      c.set(x + 1, y, cap ? 'Y' : Math.abs(y - 20) < 3 ? '4' : '3');
      if (Math.abs(y - 20) < 6) c.set(x + 2, y, '2');
    }
    // The bolt on the tiller (gone just after it flies): a walnut shaft, white fletching at its back, its gold head
    // at the muzzle, a glint running along it.
    if (state !== 'loose') {
      const from = state === 'cocking' ? 40 : 32;
      for (let x = from; x <= 57; x++) {
        c.set(x, 18, Math.abs(x - glint) < 1.5 ? 'w' : 't');
        c.set(x, 19, 'm');
      }
      if (state === 'drawn') {
        c.set(33, 16, 'w');
        c.set(34, 16, 'w');
        c.set(33, 17, '5');
        c.set(34, 17, 'w');
      }
      taper(c, 58, 63, 18.5, 2.5, 0.5, (top, bottom): PaletteKey => (top === 0 ? 'y' : bottom === 0 ? 'U' : 'Y'));
    }
  });

export default sprite({
  name: 'pollinator_knight',
  ...geometry,
  notes: `Arbalest (ADR-0160 amended, the Knight set's gun): a steel crossbow held level. A walnut stock lit on top with a
steel butt plate, a long steel trigger lever under it, a walnut tiller to the bow with a gold-capped nut, the bow's steel
limbs standing across it near the muzzle and swept back to gold-capped tips, the string drawn back from them to the
nut, a bolt on the tiller (a pale shaft, white fletching, its gold head at the muzzle) with a glint running along it
(4 frames). Fire: the string snaps straight across the bow and the bolt is gone, then the string is drawn back and a
bolt laid, over 4 frames. Its flair: the gold head's glow, a glint on the bow. No team keys. Rubric (4×): S4 R4 P4 L4
C4 A4 F4.`,
  anims: {
    idle: {
      fps: 8,
      frames: [arbalest(36, 'drawn'), arbalest(42, 'drawn'), arbalest(48, 'drawn'), arbalest(54, 'drawn')],
    },
    fire: {
      fps: 20,
      loop: false,
      frames: [arbalest(0, 'loose'), arbalest(0, 'loose'), arbalest(0, 'cocking'), arbalest(0, 'drawn')],
    },
  },
});
